import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useProfile } from '@/api/profile'

export interface DossierClio {
  id: string
  display_number: string
  description: string
  /** hourly (à l'heure), flat (forfait), contingency (pourcentage), ou vide. */
  billing_method: string
}

export interface DossiersClio {
  matters: DossierClio[]
  /** Par identifiant d'appel : le dossier retenu, et s'il a été choisi automatiquement. */
  calls: Record<string, { matter_id: string; auto: boolean }>
}

interface ResultatChangement {
  call_id: number
  matter_id: string
  auto: boolean
  time_entry: boolean
}

const cleDossiers = (companyId: number | undefined, phone: string | null | undefined) => ['clio-dossiers', companyId, phone]

/**
 * Les dossiers Clio ouverts du client d'un fil, et le dossier de chacun de
 * ses appels. Une requête par fil, et seulement si Clio est connecté : l'état
 * partage sa clé avec le panneau Clio des intégrations.
 */
export function useDossiersClio(phone: string | null | undefined) {
  const { data: profile } = useProfile()
  const companyId = profile?.company
  const { data: etat } = useQuery({
    queryKey: ['clio', companyId],
    queryFn: async () => (await api.get<{ connected: boolean; is_active?: boolean }>(`/api/companies/${companyId}/clio/`)).data,
    enabled: Boolean(companyId),
    staleTime: 5 * 60 * 1000,
  })
  return useQuery({
    queryKey: cleDossiers(companyId, phone),
    queryFn: async () =>
      (await api.get<DossiersClio>(`/api/companies/${companyId}/clio/matters/`, { params: { phone } })).data,
    enabled: Boolean(etat?.connected && etat?.is_active && phone),
    staleTime: 2 * 60 * 1000,
  })
}

/** Rattache un appel à un autre dossier ouvert de son client. */
export function useChangerDossierClio(phone: string | null | undefined) {
  const queryClient = useQueryClient()
  const { data: profile } = useProfile()
  const companyId = profile?.company
  return useMutation({
    mutationFn: async ({ callId, matterId }: { callId: number; matterId: string }) =>
      (await api.post<ResultatChangement>(`/api/companies/${companyId}/clio/calls/${callId}/matter/`, { matter_id: matterId })).data,
    onSuccess: (res) => {
      queryClient.setQueryData<DossiersClio>(cleDossiers(companyId, phone), (avant) =>
        avant
          ? { ...avant, calls: { ...avant.calls, [String(res.call_id)]: { matter_id: res.matter_id, auto: res.auto } } }
          : avant,
      )
    },
  })
}
